// Dashboard.jsx
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import {
  AdminPanelSettings as AdminIcon,
  NoteAdd as AdmissionAddIcon,
  People as AttendersIcon,
  LocalHospital as DoctorIcon,
  PersonAdd as PatientAddIcon,
  Accessibility as PatientIcon,
  Badge as StaffIcon,
  TrendingUp as TodayIcon
} from '@mui/icons-material';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { APP_LINK } from '../common/menu/menuItems/menuList';
import { fetchAdmissions } from '../redux/admissions/admissionsActions';
import { fetchAdminCountRequest } from '../redux/dashboard/dashboardAction';
import {
  fetchHistoryPatients,
  fetchPatients,
} from '../redux/patient/patientAction';
import { fetchSecurityOfficers } from '../redux/secuityOfficer/securityAction';

// Import component files
import ActivityTable from './admindashboard/ActivityTable';
import PatientTable from './admindashboard/PatientTable';
import SecurityTable from './admindashboard/SecurityTable';
import StatCard from './admindashboard/StatCard';

const Dashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientData, setPatientData] = useState([]);
  const [admissionData, setAdmissionData] = useState([]);

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 1:
        return 'Active';
      case 2:
        return 'In Active';
      case 3:
        return 'Discharged';
      default:
        return 'Unknown';
    }
  };

  const patientList = useSelector(
    (state) => state?.patients?.fetchListPatients || {}
  );

  const historyState = useSelector(
    (state) => state?.patients?.historyPatient || {}
  );

  const adminState = useSelector(
    (state) => state?.dashboard?.fetchAdminCount || {}
  );
  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  const securityOfficerList = securityOfficerState?.data?.data || [];

  const countData = adminState?.count?.count || {};


  
  const dashboardStats = [
    {
      title: 'Total Doctors',
      count: countData.doctorCount || 0,
      icon: <DoctorIcon sx={{ fontSize: 40 }} />,
      color: '#2196f3',
    },
    {
      title: 'Total Patients',
      count: countData.patientsCount || 0,
      icon: <PatientIcon sx={{ fontSize: 40 }} />,
      color: '#673ab7',
    },
    {
      title: 'Total Staff',
      count: countData.staffsCount || 0,
      icon: <StaffIcon sx={{ fontSize: 40 }} />,
      color: '#ff9800',
    },
    {
      title: 'Admin',
      count: 1,
      icon: <AdminIcon sx={{ fontSize: 40 }} />,
      color: '#4caf50',
    },
  ];

  const todayStats = [
    
    {
      title: "Total users",
      count: countData.totalUsersCount || 0,
      icon: <TodayIcon sx={{ fontSize: 40 }} />,
      color: '#e91e63',
    },
    {
      title: "Total Security Officers",
      count: countData.securityCount || 0,
      icon: <AttendersIcon sx={{ fontSize: 40 }} />,
      color: '#009688',
    },{
      title: "Today's Patients",
      count: countData.todayPatientsCount || 0,
      icon: <TodayIcon sx={{ fontSize: 40 }} />,
      color: '#e91e63',
    },
    {
      title: "Today's Attenders",
      count: countData.todayattenderCount || 0,
      icon: <AttendersIcon sx={{ fontSize: 40 }} />,
      color: '#009688',
    },
  ];

  const historyData = historyState?.data?.data || [];
  const handleAddPatient = () => {
    navigate(APP_LINK.ADDPATIENT);
  };

  const handleAddAdmission = () => {
    navigate(APP_LINK.ADMISSIONDETAILS);
  };
  const handleReport = () => {
    navigate(APP_LINK.DAILYREPORT);
  }

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchPatients(currentPage));
      await dispatch(fetchAdminCountRequest(currentPage));
      await dispatch(fetchSecurityOfficers(currentPage));
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchHistoryPatients(currentPage));
  }, [dispatch, currentPage]);

  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAdmissionsAll || {}
  );

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchAdmissions(currentPage));
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.id,
        admissionNo: admission.admission_no,
        patientId: admission.Patient?.id,
        patientSerialNo: admission.Patient?.patient_serial_no,
        patientName: admission.Patient?.patient_name,
        ipNumber: admission.Patient?.ip_number,
        phone: admission.Patient?.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockName: admission.Block?.block_name || 'N/A',
        floorName: admission.Floor?.floor_name || 'N/A',
        departmentName: admission.Department?.department_name || 'N/A',
        wardName: admission.Ward?.ward_name || 'N/A',
        status: getStatusLabel(admission.admission_status),
        numAttenders: admission.number_attenders || 0,
        attenders: admission.Attenders || [],
      }));
      setAdmissionData(formattedData);
    } else {
      setAdmissionData([]);
    }
  }, [admissionsList]);

  useEffect(() => {
    if (patientList.data && Array.isArray(patientList.data.data)) {
      const formattedData = patientList.data.data.map((patient) => ({
        ids: patient.patient_serial_no,
        id: patient.id,
        name: patient.patient_name,
        ipNumber: patient.ip_number,
        phone: patient.phone,
        joinDate: patient.createdAt,
        dischargeDate: patient.discharge_date || 'N/A',
        status:
          patient.User.status === 1
            ? 'Active'
            : patient.User.status === 2
            ? 'Inactive'
            : 'Unknown',
      }));
      setPatientData(formattedData);
    } else {
      setPatientData([]);
    }
  }, [patientList]);

  useEffect(() => {
    if (historyData.length > 0) {
      const processedData = historyData.map((item) => ({
        id: item.id,
        serialNumber: item.Patient?.patient_serial_no || 'Unknown',
        patientName: item.Patient?.patient_name || 'Unknown',
        attendantName: item.Attender?.attender_name || 'Unknown',
        status: item.attender_status === 2 ? 'In' : 'Out',
        block: item.Block?.block_name || 'Unknown',
        floor: item.Floor?.floor_name || 'Unknown',
        ward: item.Ward?.ward_name || 'Unknown',
        phone: item.Attender?.attender_phone || 'Unknown',
      }));

      setFilteredData(processedData);
    }
  }, [historyData]);


  const containerVariant = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const buttonVariant = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.1)",
    },
    tap: {
      scale: 0.95,
    }
  };


  const TodayStatCard = ({ stat }) => (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(135deg, ${stat.color}22 0%, ${stat.color}11 100%)`,
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${stat.color}33`,
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 10px 20px ${stat.color}33`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -15,
          right: -15,
          width: 90,
          height: 90,
          borderRadius: '50%',
          background: `${stat.color}22`,
          zIndex: 0,
        }}
      />
      
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: stat.color }}>
            {stat.count}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
            {stat.title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: stat.color,
            width: 56,
            height: 56,
            color: 'white',
          }}
        >
          {stat.icon}
        </Box>
      </Box>
      
      <Box sx={{ mt: 2 }}>
        <Divider />
        <Typography 
          variant="body2" 
          sx={{ 
            pt: 1, 
            color: 'text.secondary', 
            display: 'flex', 
            alignItems: 'center' 
          }}
        >
          <TodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Activity Today
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Box
        component="main"
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
        }}
      >
        {/* Header with title and action buttons */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            alignItems: { xs: 'flex-start', md: 'center' }, 
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#222',
                textShadow: '0px 2px 3px rgba(0,0,0,0.1)',
                position: 'relative',
                mb: { xs: 3, md: 0 },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  bottom: -10,
                  width: 60,
                  height: 4,
                  borderRadius: 2,
                  background: '#129bab',
                },
              }}
            >
              Inpatient Attendee Management Dashboard
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, md: 0 } }}>
            <motion.div whileHover="hover" whileTap="tap" variants={buttonVariant}>
                <Button
                  variant="contained"
                  startIcon={<PatientAddIcon />}
                  onClick={handleReport}
                  sx={{
                    background: '#e91e63',
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 10px rgba(243, 33, 54, 0.3)',
                    color: 'white',
                    '&:hover': {
                      background: '#1976d2',
                    }
                  }}
                >
                 Report
                </Button>
              </motion.div>
              <motion.div whileHover="hover" whileTap="tap" variants={buttonVariant}>
                <Button
                  variant="contained"
                  startIcon={<PatientAddIcon />}
                  onClick={handleAddPatient}
                  sx={{
                    background: '#2196f3',
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
                    color: 'white',
                    '&:hover': {
                      background: '#1976d2',
                    }
                  }}
                >
                  Add Patient
                </Button>
              </motion.div>
              
              <motion.div whileHover="hover" whileTap="tap" variants={buttonVariant}>
                <Button
                  variant="contained"
                  startIcon={<AdmissionAddIcon />}
                  onClick={handleAddAdmission}
                  sx={{
                    background: '#ff6f00',
                    color: 'white',
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 10px rgba(255, 111, 0, 0.3)',
                    '&:hover': {
                      background: '#e65100',
                    }
                  }}
                >
                  New Admission
                </Button>
              </motion.div>
            </Box>
          </Box>
        </motion.div>

       

        {/* Stats Cards */}
        <motion.div
          variants={containerVariant}
          initial="hidden"
          animate="visible"
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              color: '#333',
            }}
          >
            <DoctorIcon sx={{ mr: 1, color: '#2196f3' }} />
            Hospital Overview
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {dashboardStats.map((stat, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
              >
                <motion.div variants={itemVariant}>
                  <StatCard stat={stat} index={index} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
 {/* Today's Stats - New Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                color: '#333',
              }}
            >
              <TodayIcon sx={{ mr: 1, color: '#e91e63' }} />
              Today's Activity
            </Typography>
            
            <Grid container spacing={3}>
              {todayStats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={`today-${index}`}>
                  <TodayStatCard stat={stat} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <PatientTable patientData={patientData.slice(0, 5)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SecurityTable securityOfficerList={securityOfficerList.slice(0, 5)} />
            </Grid>
          </Grid>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5 }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ActivityTable admissionData={admissionData.slice(0, 5)} />
            </Grid>
          </Grid>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Dashboard;